import React, { useCallback, useEffect, useRef, useState } from 'react';
import { vipAPI } from '../../../services/api';
import { useBrand } from '../../../context/BrandContext';
import { Button } from '../../ui/button';
import { cn } from '../../../lib/utils';
import ContactLogPanel, { SavedToast } from './ContactLogPanel';
import { formatShortDay } from './trackerView';

const FEATURE = 'vip.contact_tracker';
const TOAST_MS = 6000;

// usePlayerContactLog: the profile's contact logging. button (the latest contact and the "Log"
// toggle) goes on the player card; panel (the log form with history, or the saved toast) goes
// under it. Both are null for brands that list vip.contact_tracker as unsupported, and when the
// caller disables it (the private VIP dashboard has no contact log).
export function usePlayerContactLog({ userId, playerName, ngr30d, enabled = true }) {
  const { activeBrand, activeBrandInfo } = useBrand();
  const supported = enabled && !(activeBrandInfo?.unsupported || []).includes(FEATURE);
  const [latest, setLatest] = useState(null);
  const [open, setOpen] = useState(false);
  const [toast, setToast] = useState('');
  const toastTimer = useRef(null);

  const loadLatest = useCallback((signal) => {
    vipAPI.getPlayerContacts(userId, signal)
      .then((res) => setLatest((res.data.contacts || [])[0] || null))
      .catch(() => { if (!signal?.aborted) setLatest(null); });
  }, [userId]);

  useEffect(() => {
    if (!supported) return undefined;
    setOpen(false);
    const controller = new AbortController();
    loadLatest(controller.signal);
    return () => controller.abort();
  }, [supported, activeBrand, loadLatest]);

  useEffect(() => () => clearTimeout(toastTimer.current), []);

  const onSaved = ({ toast: text }) => {
    setOpen(false);
    setToast(text);
    clearTimeout(toastTimer.current);
    toastTimer.current = setTimeout(() => setToast(''), TOAST_MS);
    loadLatest();
  };

  if (!supported) return { button: null, panel: null };

  const button = (
    <div className="ml-auto flex shrink-0 items-center gap-3">
      <span className="text-[13px] text-ink-dim">
        Last contact: <span className="text-ink">{latest ? `${formatShortDay(latest.contacted_at)} · ${latest.channel}` : 'never'}</span>
      </span>
      <Button
        type="button" variant="outline" aria-expanded={open} aria-label={`Log contact for ${playerName}`}
        className={cn('h-11 min-w-[76px] border-cta text-cta hover:bg-cta/10 hover:text-cta', open && 'bg-cta/10')}
        onClick={() => { setOpen(!open); setToast(''); }}
      >
        Log
      </Button>
    </div>
  );

  const row = { user_id: userId, username: playerName, ngr_30d: ngr30d, last_contact: latest };
  let panel = null;
  if (open) {
    panel = <ContactLogPanel key={userId} row={row} className="" onCancel={() => setOpen(false)} onSaved={onSaved} />;
  } else if (toast) {
    panel = <SavedToast text={toast} />;
  }
  return { button, panel };
}
